import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export type ProviderLevel = 'RBT' | 'BCBA' | 'Any' | '';

export interface ServiceType {
    id: string;
    serviceCodeId: string | null;
    code: string;                   // denormalized from the service_codes join — display only
    name: string;
    provider: ProviderLevel;
    billable: boolean;
    billRate: number;
    allowRate: number;
    mod1: string;
    mod2: string;
    unitDur: number;
    status: 'Active' | 'Inactive';
}

export interface ServiceTypeWritePayload {
    serviceCodeId?: string;
    name?: string;
    provider?: ProviderLevel;
    billable?: boolean;
    billRate?: number;
    allowRate?: number;
    mod1?: string;
    mod2?: string;
    unitDur?: number;
    status?: 'Active' | 'Inactive';
}

function normalizeStatus(s: string): 'Active' | 'Inactive' {
    return String(s).toLowerCase() === 'active' ? 'Active' : 'Inactive';
}

function normalize(row: ServiceType & { status: string }): ServiceType {
    return { ...row, status: normalizeStatus(row.status) };
}

export function useServiceTypes() {
    return useQuery({
        queryKey: ['service-types'] as const,
        queryFn: async () => {
            const res = await api.get<Array<ServiceType & { status: string }>>('/service-types');
            return res.data.map(normalize);
        },
        staleTime: 60_000,
    });
}

export function useCreateServiceType() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: ServiceTypeWritePayload) => {
            const res = await api.post('/service-types', payload);
            return normalize(res.data);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['service-types'] }),
    });
}

export function useUpdateServiceType() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: ServiceTypeWritePayload }) => {
            const res = await api.patch(`/service-types/${id}`, payload);
            return normalize(res.data);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['service-types'] }),
    });
}

export function useDeleteServiceType() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/service-types/${id}`);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['service-types'] }),
    });
}
